import React, { useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import ChevronUp from 'mdi-react/ChevronUpIcon';
import ChevronDown from 'mdi-react/ChevronDownIcon';
import CloseIcon from 'mdi-react/CloseIcon';
import ProductImage from '@prisjakt/ui-product-image';
import T from '@prisjakt/ui-translate';
import CompareIcon from 'mdi-react/ScaleBalanceIcon';
import {
  trackComparisonLinkClick,
  ChangeComparisonSelectionSource,
} from '../../areas/product-compare/tracking';
import * as S from './styles';
import { ProductSelectedToCompare } from '../../hooks/compare-products-selection';

type Props = {
  products: ProductSelectedToCompare[];
  compareUrl?: string;
  onEmpty: () => void;
  onProductDelete: (
    productId: number,
    source: ChangeComparisonSelectionSource
  ) => void;
};

const CompareProductsBasket = ({
  products,
  compareUrl,
  onEmpty,
  onProductDelete,
}: Props) => {
  const [expanded, setExpanded] = useState(false);
  const toggleExpanded = useCallback(() => setExpanded(!expanded), [expanded]);
  const compare = useCallback(
    e => {
      e.stopPropagation();
      trackComparisonLinkClick(products.length);
    },
    [products.length]
  );
  const compareComponent = useMemo(
    // This component is passed as a prop and given a label via that component instead of here.
    // eslint-disable-next-line jsx-a11y/control-has-associated-label
    () => (compareUrl ? <Link to={compareUrl} /> : <button type="button" />),
    [compareUrl]
  );
  const productsWithImage = useMemo(
    () =>
      products.map(product => ({
        ...product,
        image: (
          <ProductImage name={product.name} src={product.media.first || ''} />
        ),
      })),
    [products]
  );

  return (
    <S.Container>
      <S.Header onClick={toggleExpanded}>
        <S.FlexWrapper>
          <S.ExpandIcon>
            {expanded ? <ChevronDown /> : <ChevronUp />}
          </S.ExpandIcon>
          {products.length === 0 ? (
            <S.InfoText>
              <T domain="product">product-compare.choose-products</T>
            </S.InfoText>
          ) : (
            <S.ProductCount>
              <T domain="lists" n={products.length}>
                list.n-products
              </T>
            </S.ProductCount>
          )}
        </S.FlexWrapper>
        <S.CompareButton
          size="small"
          color="primary"
          variant="outlined"
          iconPosition="left"
          disabled={products.length < 2}
          onClick={compare}
          component={compareComponent}
        >
          <CompareIcon size={18} />
          <T domain="product">product-compare.compare</T>
        </S.CompareButton>
      </S.Header>
      <S.ProductWrapper count={products.length} expanded={expanded}>
        <S.InfoBar>
          {products.length < 2 && (
            <S.InfoText>
              <T domain="product">product-compare.choose-more-products</T>
            </S.InfoText>
          )}
          <S.ClearButton variant="text" color="link" onClick={onEmpty}>
            <T domain="product">product-compare.cancel-compare</T>
          </S.ClearButton>
        </S.InfoBar>
        {productsWithImage.map(product => (
          <CompareProductsBasketRow
            key={product.id}
            product={product}
            onProductDelete={onProductDelete}
          />
        ))}
      </S.ProductWrapper>
    </S.Container>
  );
};

type RowProps = {
  product: ProductSelectedToCompare;
  onProductDelete: (
    productId: number,
    source: ChangeComparisonSelectionSource
  ) => void;
};

function CompareProductsBasketRow({ product, onProductDelete }: RowProps) {
  const productImage = useMemo(
    () => <ProductImage name={product.name} src={product.media.first || ''} />,
    [product.name, product.media.first]
  );
  const onProductClick = useCallback(e => e.preventDefault(), []);
  const closeIcon = useMemo(() => <CloseIcon />, []);
  const onDelete = useCallback(() => onProductDelete(product.id, 'basket'), [
    onProductDelete,
    product.id,
  ]);

  return (
    <S.ProductRow
      key={product.id}
      link={product.pathName}
      onClick={onProductClick}
      name={product.name}
      image={productImage}
      price={product.priceSummary.regular || product.priceSummary.alternative}
      showDivider
    >
      <S.ProductDeleteButton
        size="small"
        variant="icon"
        color="primary"
        icon={closeIcon}
        onClick={onDelete}
      />
    </S.ProductRow>
  );
}

export default CompareProductsBasket;
